import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import colors from '../../theme/colors';
import { getMeetups, Meetup } from '../../services/firebaseService';
import EventCard from './EventCard';
import MeetupModal from './MeetupModal';

const EventsSection: React.FC = () => {
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMeetup, setSelectedMeetup] = useState<Meetup | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMeetups = async () => {
      try {
        setLoading(true);
        const fetchedMeetups = await getMeetups();
        setMeetups(fetchedMeetups);
      } catch (err) {
        setError('Failed to fetch meetups. Please try again later.');
        console.error('Error fetching meetups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetups();
  }, []);

  const handleShowAllEvents = () => {
    navigate('/events');
  };

  const handleOpenModal = (meetup: Meetup) => {
    setSelectedMeetup(meetup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMeetup(null);
  };



  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: colors.green }} />
        <Typography variant="h6" sx={{ mt: 2, color: colors.textSecondary }}>
          Loading events...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body2" color={colors.textSecondary}>
          Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h3" color="primary" fontWeight={700} gutterBottom>
          Past Events
        </Typography>
        <Box sx={{ height: 4, width: 48, borderRadius: 2, background: colors.accent, mx: 'auto', mb: 6 }} />
        
        {meetups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color={colors.textSecondary} gutterBottom>
              No events found
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              Check back later for upcoming events!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {meetups.slice(0, 6).map((meetup) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={meetup.id || meetup.title}>
                <EventCard 
                  meetup={meetup} 
                  onReadMore={handleOpenModal}
                  showReadMore={true}
                />
              </Grid>
            ))}
          </Grid>
        )}
        
        {/* Show All Events Button */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleShowAllEvents}
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
            }}
          >
            Show All Events
          </Button>
        </Box>
      </Container>

      {/* Meetup Details Modal */}
      <MeetupModal
        open={modalOpen}
        onClose={handleCloseModal}
        meetup={selectedMeetup}
      />
    </>
  );
};

export default EventsSection; 